import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'

export function createScene(container) {
  const width = Math.max(container.clientWidth, 1)
  const height = Math.max(container.clientHeight, 1)

  const scene = new THREE.Scene()
  scene.background = new THREE.Color(0x0f172a)
  scene.fog = new THREE.Fog(0x0f172a, 8, 20)

  const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100)
  camera.position.set(0, 0.5, 4)

  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
  renderer.setSize(width, height)
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
  renderer.localClippingEnabled = true
  renderer.shadowMap.enabled = true
  renderer.shadowMap.type = THREE.PCFSoftShadowMap
  container.appendChild(renderer.domElement)

  const controls = new OrbitControls(camera, renderer.domElement)
  controls.enableDamping = true
  controls.dampingFactor = 0.06
  controls.rotateSpeed = 0.85
  controls.panSpeed = 0.85
  controls.zoomSpeed = 1.0
  controls.minDistance = 1.2
  controls.maxDistance = 12
  controls.target.set(0, 0.2, 0)
  // 右键旋转，左键平移，滚轮缩放
  controls.mouseButtons = {
    LEFT: THREE.MOUSE.PAN,
    MIDDLE: THREE.MOUSE.DOLLY,
    RIGHT: THREE.MOUSE.ROTATE,
  }

  // 灯光
  const ambient = new THREE.AmbientLight(0xffffff, 0.55)
  scene.add(ambient)

  const mainLight = new THREE.DirectionalLight(0xffffff, 1.0)
  mainLight.position.set(5, 8, 5)
  mainLight.castShadow = true
  scene.add(mainLight)

  const fillLight = new THREE.DirectionalLight(0x93c5fd, 0.45)
  fillLight.position.set(-4, 2, -3)
  scene.add(fillLight)

  const rimLight = new THREE.PointLight(0xfbbf24, 0.35, 15)
  rimLight.position.set(0, -2, 3)
  scene.add(rimLight)

  // 网格地面
  const grid = new THREE.GridHelper(10, 20, 0x334155, 0x1e293b)
  grid.position.y = -1.5
  scene.add(grid)

  // 剖切平面
  const clipPlane = new THREE.Plane(new THREE.Vector3(0, 0, -1), 0)
  const clipPlaneHelper = new THREE.PlaneHelper(clipPlane, 2.5, 0x38bdf8)
  clipPlaneHelper.visible = false
  scene.add(clipPlaneHelper)

  return { scene, camera, renderer, controls, clipPlane, clipPlaneHelper }
}

export function applyClippingToModel(root, clipPlane, enabled) {
  root.traverse((obj) => {
    if (obj.isMesh && obj.material) {
      const materials = Array.isArray(obj.material) ? obj.material : [obj.material]
      materials.forEach((mat) => {
        mat.clippingPlanes = enabled ? [clipPlane] : []
        mat.clipShadows = enabled
        mat.needsUpdate = true
      })
    }
  })
}

export function fitCameraToBounds(camera, controls, box, padding = 1.45) {
  if (!box || box.isEmpty()) {
    controls.target.set(0, 0, 0)
    camera.position.set(0, 0, 4)
    controls.update()
    return { center: new THREE.Vector3(0, 0, 0), distance: 4 }
  }
  const center = box.getCenter(new THREE.Vector3())
  const size = box.getSize(new THREE.Vector3())
  const maxDim = Math.max(size.x, size.y, size.z, 0.001)
  const vFov = (camera.fov * Math.PI) / 180
  const aspect = camera.aspect || 1
  const hFov = 2 * Math.atan(Math.tan(vFov / 2) * aspect)
  const distForHeight = (size.y / 2) / Math.tan(vFov / 2)
  const distForWidth = (size.x / 2) / Math.tan(hFov / 2)
  const distance = Math.max(distForHeight, distForWidth, maxDim * 0.6) * padding
  controls.target.copy(center)
  camera.position.set(center.x, center.y, center.z + distance)
  controls.minDistance = Math.max(maxDim * 0.35, 0.8)
  controls.maxDistance = Math.max(maxDim * 4, 8)
  controls.update()
  return { center: center.clone(), distance }
}

export function animateCameraTo(camera, controls, target, distance, duration = 1200) {
  return new Promise((resolve) => {
    const startPos = camera.position.clone()
    const startTarget = controls.target.clone()
    const direction = new THREE.Vector3()
      .subVectors(startPos, startTarget)
      .normalize()
    const endTarget = target.clone()
    const endPos = endTarget.clone().add(direction.multiplyScalar(distance))
    const startTime = performance.now()

    function step(now) {
      const t = Math.min((now - startTime) / duration, 1)
      const eased = 1 - Math.pow(1 - t, 3)
      controls.target.lerpVectors(startTarget, endTarget, eased)
      camera.position.lerpVectors(startPos, endPos, eased)
      controls.update()
      if (t < 1) {
        requestAnimationFrame(step)
      } else {
        resolve()
      }
    }
    requestAnimationFrame(step)
  })
}

export function pickObject(event, container, camera, objects) {
  const rect = container.getBoundingClientRect()
  const mouse = new THREE.Vector2(
    ((event.clientX - rect.left) / rect.width) * 2 - 1,
    -((event.clientY - rect.top) / rect.height) * 2 + 1
  )
  const raycaster = new THREE.Raycaster()
  raycaster.params.Points = { threshold: 0.15 }
  raycaster.params.Line = { threshold: 0.15 }
  raycaster.setFromCamera(mouse, camera)
  const hits = raycaster.intersectObjects(objects, true)
  hits.sort((a, b) => {
    const aMarker = a.object.userData.isHotspot || a.object.userData.isHotspotDot || a.object.userData.isLesionMarker
    const bMarker = b.object.userData.isHotspot || b.object.userData.isHotspotDot || b.object.userData.isLesionMarker
    if (aMarker && !bMarker) return -1
    if (!aMarker && bMarker) return 1
    return a.distance - b.distance
  })
  return hits
}

export function pulseHotspots(hotspots, time) {
  hotspots.forEach((hs) => {
    if (hs.pulseMesh) {
      const scale = 1 + Math.sin(time * 0.003) * 0.3
      hs.pulseMesh.scale.setScalar(scale)
      hs.pulseMesh.material.opacity = 0.2 + Math.sin(time * 0.003) * 0.15
    }
  })
}
